import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Briefcase, Loader2, ArrowUpRight, Upload } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const EASE = [0.16, 1, 0.3, 1];

const inputClass =
  "w-full bg-navy-800/60 border border-white/10 rounded-none h-12 px-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-electric";
const labelClass = "font-mono-accent text-[10px] tracking-[0.25em] uppercase text-slate-500 block mb-2";

export default function Careers() {
  useSEO({
    title: "Careers",
    path: "/careers",
    description:
      "Join GENOUM GROUP — current openings across renewable energy, green hydrogen, EPC and medical technology in Bhubaneswar, Balasore and Ghaziabad.",
  });
  const [openings, setOpenings] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", position: "", message: "" });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    api.get("/careers/openings")
      .then((res) => {
        setOpenings(res.data);
        setForm((f) => ({ ...f, position: res.data.length ? res.data[0].title : "General Application" }));
      })
      .catch(() => setOpenings([]));
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const applyFor = (title) => {
    setForm((f) => ({ ...f, position: title }));
    const el = document.querySelector("#apply");
    if (el) {
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
      else el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (resume) fd.append("resume", resume);
      await api.post("/careers/applications", fd);
      toast.success("Application received. Our team will review and reach out.");
      setForm({ name: "", email: "", phone: "", position: openings?.[0]?.title || "", message: "" });
      setResume(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-navy min-h-screen text-slate-50" data-testid="careers-page">
      <Navbar />
      <main className="pt-36 pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-mono-accent text-xs tracking-[0.35em] uppercase text-neon mb-6"
          >
            Careers at Genoum
          </motion.p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-[0.95]" data-testid="careers-heading">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" initial={{ y: "115%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.15, ease: EASE }}>
                Build what
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span className="block text-transparent bg-clip-text bg-gradient-to-r from-electric to-neon" initial={{ y: "115%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.3, ease: EASE }}>
                comes next.
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-base md:text-lg text-slate-400 leading-relaxed mt-8 max-w-2xl"
          >
            Join a growing engineering-led group delivering solar plants, green hydrogen,
            turnkey EPC and medical technology across India. We hire for precision,
            accountability and hunger to build.
          </motion.p>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-24">
          <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-8">Open Positions</p>
          {!openings ? (
            <div className="flex justify-center py-16" data-testid="openings-loading">
              <Loader2 className="w-6 h-6 text-electric animate-spin" />
            </div>
          ) : (
            <div className="space-y-4 mb-24" data-testid="openings-list">
              {openings.length === 0 && (
                <div className="border border-white/10 bg-navy-800/40 p-8 text-sm text-slate-400" data-testid="no-openings-note">
                  No open positions right now — send us a general application below and we&apos;ll reach out when a role opens.
                </div>
              )}
              {openings.map((job, i) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 32 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
                  className="group card-glow border border-white/10 bg-navy-800/40 p-6 lg:p-7 flex flex-col md:flex-row md:items-center gap-5"
                  data-testid={`opening-card-${i}`}
                >
                  <div className="flex-1">
                    <h3 className="font-display text-lg lg:text-xl font-bold tracking-tight">{job.title}</h3>
                    <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-slate-500 mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                      <span className="flex items-center gap-1.5"><Briefcase className="w-3 h-3 text-electric" /> {job.division}</span>
                      <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3 text-electric" /> {job.location}</span>
                      <span className="text-neon">{job.type}</span>
                    </p>
                  </div>
                  <button
                    onClick={() => applyFor(job.title)}
                    className="font-mono-accent text-[10px] tracking-[0.2em] uppercase border border-white/15 group-hover:border-neon group-hover:text-neon text-slate-300 px-6 py-3 flex items-center gap-2 transition-colors duration-300 w-fit"
                    data-testid={`apply-button-${i}`}
                  >
                    Apply Now <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                </motion.div>
              ))}
            </div>
          )}

          <div id="apply" className="grid grid-cols-1 lg:grid-cols-12 gap-14">
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: EASE }}
              className="lg:col-span-5"
            >
              <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-6">Apply</p>
              <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight leading-tight">
                Tell us where <span className="text-outline">you fit.</span>
              </h2>
              <p className="text-sm text-slate-400 leading-relaxed mt-6 max-w-sm">
                Share your details and resume. Shortlisted candidates hear back from our
                team within a few working days.
              </p>
            </motion.div>

            <motion.form
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
              className="lg:col-span-7 space-y-6"
              data-testid="careers-form"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Name</label>
                  <input name="name" value={form.name} onChange={handleChange} required placeholder="Your full name" className={inputClass} data-testid="careers-name-input" />
                </div>
                <div>
                  <label className={labelClass}>Email</label>
                  <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="you@email.com" className={inputClass} data-testid="careers-email-input" />
                </div>
                <div>
                  <label className={labelClass}>Phone</label>
                  <input name="phone" value={form.phone} onChange={handleChange} required placeholder="+91" className={inputClass} data-testid="careers-phone-input" />
                </div>
                <div>
                  <label className={labelClass}>Position</label>
                  <select name="position" value={form.position} onChange={handleChange} required className={inputClass} data-testid="careers-position-select">
                    {(openings || []).length === 0 && (
                      <option value="General Application" className="bg-navy-800">General Application</option>
                    )}
                    {(openings || []).map((job) => (
                      <option key={job.id} value={job.title} className="bg-navy-800">{job.title}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelClass}>Cover Note</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="Why you, why Genoum..."
                  className="w-full bg-navy-800/60 border border-white/10 px-3 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-electric"
                  data-testid="careers-message-input"
                />
              </div>
              <div>
                <label className={labelClass}>Resume (PDF or Word, optional)</label>
                <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase border border-neon/30 text-neon hover:bg-neon/10 px-5 py-3 inline-flex items-center gap-2 cursor-pointer transition-colors duration-300" data-testid="careers-resume-upload">
                  <Upload className="w-3.5 h-3.5" />
                  {resume ? resume.name : "Choose File"}
                  <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setResume(e.target.files?.[0] || null)} className="hidden" />
                </label>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="group font-mono-accent text-xs tracking-[0.2em] uppercase bg-electric hover:bg-electric-hover disabled:opacity-60 text-white px-10 py-4 flex items-center gap-2 transition-colors duration-300"
                data-testid="careers-submit-button"
              >
                {submitting ? (
                  <>Submitting <Loader2 className="w-4 h-4 animate-spin" /></>
                ) : (
                  <>Submit Application <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>
                )}
              </button>
            </motion.form>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
